/**
 * Q3 dust puffs on movement.
 *
 * When a mech transitions to a new tile (its `position` differs from
 * the previous tick's), spawn a short-lived puff of small cube
 * particles at the *origin* tile. Reads like the chassis kicking up
 * dust as it steps off. Lifetime 400ms; particles drift outward and
 * fall under gravity. Seeded by `(matchSeed, side, tick)` so two
 * viewings produce the same scatter.
 *
 * Standalone (no Rapier) — we own the particle integration in
 * `useFrame` over a small fixed roster (6 per puff). Cheap enough to
 * not warrant the physics-scene round-trip.
 */

import { useEffect, useRef, useState } from "react";
import { useFrame } from "@react-three/fiber";
import type { Hex } from "@pilot-protocol/replay-parser";
import type { Mesh } from "three";
import { SeededRng } from "../determinism/cosmeticSeed";
import { hexToWorld, TILE_THICKNESS } from "./hexMath";

export interface DustPuffsProps {
  /** Current-tick A position. */
  posA: Hex;
  /** Current-tick B position. */
  posB: Hex;
  /** Current tick — drives the dedupe key + seed. */
  tick: number;
  /** Match seed for deterministic scatter direction. */
  matchSeed: number | string;
  /** Skip rendering when true (a11y / perf). */
  disabled?: boolean;
}

interface DustParticle {
  ox: number;
  oz: number;
  oy: number;
  vx: number;
  vy: number;
  vz: number;
}

interface ActivePuff {
  id: string;
  side: "A" | "B";
  worldX: number;
  worldZ: number;
  particles: DustParticle[];
  startedAt: number;
}

export const DUST_DURATION_MS = 400;
const DUST_PARTICLES_PER_PUFF = 6;
const DUST_PARTICLE_SIZE = 0.04;
const FLOOR_OFFSET = TILE_THICKNESS + 0.02;
const DUST_COLOR_A = "#94a3b8";
const DUST_COLOR_B = "#a8a29e";

function hexKey(h: Hex): string {
  return `${h.q},${h.r}`;
}

function seedFor(matchSeed: number | string, side: "A" | "B", tick: number): number {
  let h = 0xb5297a4d;
  const ms = typeof matchSeed === "number" ? matchSeed : hashString(matchSeed);
  h = Math.imul(h ^ ms, 0x85ebca6b) >>> 0;
  h = Math.imul(h ^ (side === "A" ? 0xa : 0xb), 0xc2b2ae35) >>> 0;
  h = Math.imul(h ^ tick, 0x27d4eb2f) >>> 0;
  return h >>> 0;
}

function hashString(s: string): number {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) >>> 0;
  return h >>> 0;
}

function makePuff(
  side: "A" | "B",
  fromHex: Hex,
  tick: number,
  matchSeed: number | string,
  startedAt: number,
): ActivePuff {
  const rng = new SeededRng(seedFor(matchSeed, side, tick));
  const w = hexToWorld(fromHex, 0);
  const particles: DustParticle[] = [];
  for (let i = 0; i < DUST_PARTICLES_PER_PUFF; i++) {
    const theta = rng.next() * Math.PI * 2;
    const spread = 0.6 + rng.next() * 0.4;
    particles.push({
      ox: Math.cos(theta) * 0.1,
      oz: Math.sin(theta) * 0.1,
      oy: 0,
      vx: Math.cos(theta) * spread,
      vy: 0.8 + rng.next() * 0.5,
      vz: Math.sin(theta) * spread,
    });
  }
  return {
    id: `dust-${tick}-${side}`,
    side,
    worldX: w.x,
    worldZ: w.z,
    particles,
    startedAt,
  };
}

export function DustPuffs({ posA, posB, tick, matchSeed, disabled = false }: DustPuffsProps) {
  const [puffs, setPuffs] = useState<ActivePuff[]>([]);
  const prevPosARef = useRef<Hex | null>(null);
  const prevPosBRef = useRef<Hex | null>(null);
  const seenRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (disabled) return;
    const fresh: ActivePuff[] = [];
    const now = performance.now();
    const prevA = prevPosARef.current;
    const prevB = prevPosBRef.current;
    if (prevA && hexKey(prevA) !== hexKey(posA)) {
      const id = `dust-${tick}-A`;
      if (!seenRef.current.has(id)) {
        seenRef.current.add(id);
        fresh.push(makePuff("A", prevA, tick, matchSeed, now));
      }
    }
    if (prevB && hexKey(prevB) !== hexKey(posB)) {
      const id = `dust-${tick}-B`;
      if (!seenRef.current.has(id)) {
        seenRef.current.add(id);
        fresh.push(makePuff("B", prevB, tick, matchSeed, now));
      }
    }
    prevPosARef.current = posA;
    prevPosBRef.current = posB;
    if (fresh.length > 0) setPuffs((p) => [...p, ...fresh]);
  }, [posA, posB, tick, matchSeed, disabled]);

  useFrame(() => {
    if (puffs.length === 0) return;
    const now = performance.now();
    const live = puffs.filter((p) => now - p.startedAt < DUST_DURATION_MS);
    if (live.length !== puffs.length) setPuffs(live);
  });

  if (disabled || puffs.length === 0) return null;

  return (
    <group name="dust-puffs">
      {puffs.map((puff) => (
        <Puff key={puff.id} puff={puff} />
      ))}
    </group>
  );
}

interface PuffProps {
  puff: ActivePuff;
}

function Puff({ puff }: PuffProps) {
  return (
    <group position={[puff.worldX, FLOOR_OFFSET, puff.worldZ]}>
      {puff.particles.map((p, i) => (
        <DustParticleMesh key={i} particle={p} startedAt={puff.startedAt} side={puff.side} />
      ))}
    </group>
  );
}

function DustParticleMesh({
  particle,
  startedAt,
  side,
}: {
  particle: DustParticle;
  startedAt: number;
  side: "A" | "B";
}) {
  const meshRef = useRef<Mesh>(null);
  useFrame(() => {
    const m = meshRef.current;
    if (!m) return;
    const elapsed = (performance.now() - startedAt) / 1000;
    if (elapsed < 0) return;
    const t = Math.min(1, elapsed * (1000 / DUST_DURATION_MS));
    // Position: drift outward + ballistic Y under gravity.
    const x = particle.ox + particle.vx * elapsed;
    const z = particle.oz + particle.vz * elapsed;
    // Gravity term: -9.8/2 t^2 — keeps the puff feeling weighty.
    const y = particle.oy + particle.vy * elapsed - 4.9 * elapsed * elapsed;
    m.position.set(x, Math.max(0, y), z);
    const mat = (m.material as { opacity?: number; transparent?: boolean });
    if (typeof mat.opacity === "number") {
      mat.opacity = Math.max(0, 1 - t);
      mat.transparent = true;
    }
  });
  return (
    <mesh ref={meshRef}>
      <boxGeometry args={[DUST_PARTICLE_SIZE, DUST_PARTICLE_SIZE, DUST_PARTICLE_SIZE]} />
      <meshBasicMaterial color={side === "A" ? DUST_COLOR_A : DUST_COLOR_B} transparent />
    </mesh>
  );
}
